<template>
  <IonAccordionGroup v-model="openAccordion">
    <IonAccordion value="meterTypeForm">
      <div
        slot="header"
        class="min-h-[76px] flex items-center justify-between p-2 bg-gray-100 gap-2 border-b-2 border-white"
      >
        <img :src="`/meters/meter-plain-${utilityType.id || 1}.svg`" class="w-12 aspect-square" />
        <div class="text-gray-500 font-medium flex-1 truncate">
          <div class="text-sm leading-tight" v-if="!selectedModelHeader?.modelString">Connected Meter</div>
          <div v-if="selectedModelHeader.modelString" class="text-gray-700 text-base leading-tight truncate">
            {{ selectedModelHeader.modelString }}
          </div>
          <div v-if="selectedModelHeader.measurementString" class="text-xs text-gray-500 font-medium truncate">
            {{ selectedModelHeader.measurementString }}
          </div>
        </div>
        <div v-if="usedCount" class="text-gray-500 font-bold text-right">x{{ usedCount }}</div>
        <div
          class="transition-transform text-gray-700 text-base mr-1 size-6 flex items-center justify-center"
          :class="openAccordion === 'meterTypeForm' ? 'rotate-90' : ''"
        >
          <i class="icon-chevron-right"></i>
        </div>
      </div>
      <div slot="content" class="accordion-content">
        <MeterManufacturerTypeahead
          ref="manufacturerTypeahead"
          :state="manufacturerId.state"
          :errors="manufacturerId.errors"
          :utility-family="utilityType.family"
          :disabled="isNextMeter || isEmbeddedTransceiver || disabled"
          v-model="manufacturerId.value"
        ></MeterManufacturerTypeahead>
        <MeterModelTypeahead
          ref="modelTypeahead"
          @model-selected="onModelSelected"
          :state="meterTypeId.state"
          :errors="meterTypeId.errors"
          :utility-family="utilityType.family"
          :manufacturer-id="manufacturerId.value"
          :disabled="isNextMeter || isEmbeddedTransceiver || disabled"
          v-model="meterTypeId.value"
        ></MeterModelTypeahead>
        <InlineNumberInput
          :state="multiplier.state"
          :errors="multiplier.errors"
          v-model="multiplier.value"
          title="Multiplier"
          :clearable="false"
          :disabled="isNextMeter || disabled"
        ></InlineNumberInput>
        <InlineSelect
          v-model="uomTypeId.value"
          title="Unit of Measure"
          :options="uomOptions"
          :state="uomTypeId.state"
          :errors="uomTypeId.errors"
          :clearable="false"
          :disabled="disabled"
        ></InlineSelect>
        <InlineTextInput
          title="Meter Size"
          v-model="meterSize.value"
          :state="meterSize.state"
          :errors="meterSize.errors"
          :clearable="false"
          :disabled="isNextMeter || disabled"
        ></InlineTextInput>
      </div>
    </IonAccordion>
  </IonAccordionGroup>
</template>

<script setup lang="ts">
import InlineNumberInput from '@/components/forms/inline/InlineNumberInput.vue';
import InlineSelect from '@/components/forms/inline/InlineSelect.vue';
import InlineTextInput from '@/components/forms/inline/InlineTextInput.vue';
import MeterManufacturerTypeahead from '@/components/forms/meters/MeterManufacturerTypeahead.vue';
import MeterModelTypeahead from '@/components/forms/meters/MeterModelTypeahead.vue';
import { SelectOption } from '@/components/forms/select';
import { useFormControl } from '@/composables/use-form-control';
import { EmojiValidator } from '@/composables/validators/emoji-validator';
import { RequiredFieldValidator } from '@/composables/validators/required';
import { IonAccordion, IonAccordionGroup } from '@ionic/vue';
import {
  UtilityTypes,
  UomTypeIds,
  UomTypeHelper,
  DeviceTypeHelper,
  DeviceTypeIds,
  UomTypes,
  IUomType,
  UtilityTypeIds,
} from '@ncss/models/dist/v3';
import { MobileMeterType, UpdateMeter } from 'common';
import { computed, ref } from 'vue';

const props = defineProps<{
  utilityTypeId: UtilityTypeIds;
  manufacturerId?: number;
  meterTypeId?: number;
  multiplier?: number;
  uomTypeId?: UomTypeIds;
  meterSize?: string;
  programmedDeviceId?: number;
  usedCount?: number;
  disabled?: boolean;
}>();

const modelTypeahead = ref<InstanceType<typeof MeterModelTypeahead>>();
const manufacturerTypeahead = ref<InstanceType<typeof MeterManufacturerTypeahead>>();

const deviceType = computed(() => {
  return props.programmedDeviceId ? DeviceTypeHelper.GetDeviceTypeBySerialNumber(props.programmedDeviceId) : null;
});

const isNextMeter = computed(() => {
  return deviceType.value?.id === DeviceTypeIds.NM4_I;
});
const isEmbeddedTransceiver = computed(() => {
  return deviceType.value ? DeviceTypeHelper.GetIsEmbeddedTransceiver(deviceType.value.id) : false;
});

const utilityType = computed(() => UtilityTypes[props.utilityTypeId]);
const uomOptions = computed<SelectOption<UomTypeIds>[]>(() => {
  let uoms: IUomType[] = [];
  if (isNextMeter.value) {
    uoms = [UomTypes[UomTypeIds.GALLONS], UomTypes[UomTypeIds.LITERS]];
  } else if (utilityType.value) {
    uoms = UomTypeHelper.GetUomForUtilityTypeId(utilityType.value.id);
  }
  return uoms.map((uom) => {
    return {
      label: uom.name,
      value: uom.id,
    };
  });
});

const manufacturerId = useFormControl(() => props.manufacturerId, {
  validators: [RequiredFieldValidator()],
});
const meterTypeId = useFormControl(() => props.meterTypeId, {
  validators: [RequiredFieldValidator()],
});
const multiplier = useFormControl(() => props.multiplier, {
  validators: [RequiredFieldValidator()],
});
const uomTypeId = useFormControl(() => props.uomTypeId, {
  validators: [RequiredFieldValidator()],
});
const meterSize = useFormControl(() => props.meterSize, { validators: [EmojiValidator()] });

const openAccordion = ref<string>();

const selectedModelHeader = computed(() => {
  const uom = uomTypeId.value;
  const mult = multiplier.value;
  const size = meterSize.value;
  const modelString = modelTypeahead.value?.selectedModel?.meterTypeId
    ? modelTypeahead.value?.selectedModel?.formattedModelString()
    : 'No Model Selected';
  const measurementString = modelTypeahead.value?.selectedModel?.formattedMeasurements({
    uomTypeId: uom,
    multiplier: mult,
    meterSize: size,
  });

  return {
    modelString,
    measurementString,
  };
});

const dirty = computed<boolean>(() => {
  return manufacturerId.dirty || meterTypeId.dirty || multiplier.dirty || uomTypeId.dirty || meterSize.dirty;
});
const valid = computed<boolean>(() => {
  return manufacturerId.valid && meterTypeId.valid && multiplier.valid && uomTypeId.valid && meterSize.valid;
});

function reset() {
  manufacturerId.reset();
  meterTypeId.reset();
  multiplier.reset();
  uomTypeId.reset();
  meterSize.reset();
}

function addToUpdate(update: UpdateMeter) {
  if (manufacturerId.dirty && manufacturerId.valid) {
    update.meterManufacturerId = manufacturerId.value || null;
    update.meterManufacturerName = manufacturerTypeahead.value?.selectedManufacturer?.name || null;
  }
  if (meterTypeId.dirty && meterTypeId.valid) {
    update.meterTypeId = meterTypeId.value || null;
    update.meterModelNumber = modelTypeahead.value?.selectedModel?.meterModelNumber || null;
  }
  if (multiplier.dirty && multiplier.valid) {
    update.multiplier = multiplier.value;
  }
  if (uomTypeId.dirty && uomTypeId.valid) {
    update.uomTypeId = uomTypeId.value;
  }
  if (meterSize.dirty && meterSize.valid) {
    update.meterSize = meterSize.value || null;
  }
}

function onModelSelected(model?: MobileMeterType) {
  if (model) {
    multiplier.value = model.multiplier;
    uomTypeId.value = model.uomTypeId;
    meterSize.value = model.meterSize;
  }
}

defineExpose({
  dirty,
  valid,
  addToUpdate,
  reset,
});
</script>

<style scoped></style>
